import axios, { AxiosPromise, AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import API from "../types/api";
import HTTPConstants from '../constants/http';
import GlobalConstants from '../constants/global';
import Swal from '~/plugins/Swal';

interface ErrorResponseData {
  clientMessageHandled: boolean;
  data: {
    code: string;
    message: string;
    clientMessage?: string;
  }
}

// Create Axios instance specifically for our API
const apiAxiosInstance = axios.create({
  baseURL: '/api/v1',
  responseType: 'json',
  withCredentials: true,
});
apiAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    // https://medium.com/@brandoncc/how-to-handle-unhandledrejection-errors-using-axios-da82b54c6356
    // Prevent unhandled promise rejection errors. Our error handlers will still get called.
    throw error
  }
)

// Define base request funcs
const BaseApi = {
  makeRequest(requestObj: AxiosRequestConfig, requestOpts: API.RequestOptions): AxiosPromise {
    if (!requestOpts.skipLoader) { window.App.loader('show'); }

    const defaultHeaders = { 'Content-Type': 'application/json' }
    const headers = Object.assign(defaultHeaders, requestOpts.headers || {});

    if (headers['Content-Type'] == 'application/json') {
      // This is wrapped in a conditional to account for multipart requests, where we want to pass FormData along as is
      requestObj.data = JSON.stringify(requestObj.data);
    }

    let requestTimeout = 0;
    if (!!requestOpts.delayTimeout && !!requestOpts.delayMessage) {
      requestTimeout = setTimeout(() => {
        Swal.$swal({
          title: "Please wait",
          text: requestOpts.delayMessage,
          icon: 'info',
          customClass: {container: 'swal2-above-loader' },
        });
      }, requestOpts.delayTimeout) as any;

      const interceptor = apiAxiosInstance.interceptors.response.use((response) => {
        clearTimeout(requestTimeout);
        Swal.$swal.close();
        apiAxiosInstance.interceptors.request.eject(interceptor);
        return response;
      }, (error) => {
        clearTimeout(requestTimeout);
        Swal.$swal.close();
        apiAxiosInstance.interceptors.request.eject(interceptor);
        return Promise.reject(error);
      });
    }

    return new Promise((resolve, reject) => {
      apiAxiosInstance(Object.assign(requestObj, headers)).then(
        (success: AxiosResponse) => {
          if (success.data && success.data.sessionExpiration > 0 && window.localStorage) {
            try {
              const exp = (new Date().getTime() / 1000) + success.data.sessionExpiration;
              window.localStorage.setItem(GlobalConstants.localStorageKeys.SESSION_EXPIRATION, exp);
            } catch {
              console.error('unable to write to local storage');
            }
          }
          resolve(success.data) 
        },
        (error: AxiosError<ErrorResponseData>) => {
          if (!error.response || !error.response.data || !error.response.data.data) {
            // Something errored out, but it wasn't from our server. The load balancer or a proxy server may have failed.
            reject({data: {code: HTTPConstants.statuses.INTERNAL_SERVER_ERROR, message: 'An unexpected error has occurred.'}});
          }
          else if (error.response.data.data.message == "INVALID SESSION") {
            window.App.loader('hide');
            window.EventBus.emit('Session-expired');
          } else if (!!error.response.data.data.clientMessage) {
            if (!requestOpts.skipErrorMessaging) {
              window.App.loader('hide');
              error.response.data.clientMessageHandled = true;
              if (requestOpts.returnImmediatelyOnError) {
                Swal.$swal('Error', error.response.data.data.clientMessage, 'error');
                reject(error.response.data);
              } else {
                Swal.$swal('Error', error.response.data.data.clientMessage, 'error')
                  .then(() => {
                    reject(error.response!.data);
                  });
              }
            } else {
              reject(error.response.data);
            }
          } else {
            reject(error.response.data);
          }
        }
      );
    })
  },
  get(path: string, opts: API.RequestOptions, params?: object): AxiosPromise {
    return this.makeRequest({ url: path, method: 'GET', params: params }, opts)
  },
  delete(path: string, opts: API.RequestOptions): AxiosPromise {
    return this.makeRequest({ url: path, method: 'DELETE' }, opts)
  },
  post(path: string, data: object, opts: API.RequestOptions): AxiosPromise {
    return this.makeRequest({ url: path, data: data, method: 'POST', responseType: opts.responseType }, opts)
  },
  put(path: string, data: object, opts: API.RequestOptions): AxiosPromise {
    return this.makeRequest({ url: path, data: data, method: 'PUT' }, opts)
  },
  get baseURL() {
    return apiAxiosInstance.defaults.baseURL
  }
}

export default BaseApi;
